import { useEffect, useRef, useState } from 'react'
import { useBlocker } from 'react-router-dom'
import Modal from '@components/shared/Modal'
import PecasAcervo from '@services/pecasAcervo'
import useAcervoCatalog from './useAcervoCatalog'
import { extractPecaPdf } from './extractPecaPdf'
import { actionLabel, formatBytes } from './acervoHelpers'
import { appendBatchFiles, canImportRow, canIndexRow, importBatch, indexBatch, MAX_BATCH_FILES } from './acervoBatch'

const STATUS = {
  pending: 'Aguardando importação', invalid: 'Arquivo inválido', extracting: 'Lendo PDF',
  saving: 'Salvando original', saved: 'Original salvo', failed: 'Não importado',
  uncertain: 'Envio sem confirmação', indexing: 'Indexando', indexPending: 'Indexação em andamento',
  indexFailed: 'Original salvo · indexação pendente', ready: 'Disponível para redação',
}

export default function PecasAcervoBatchImport({ initialArea = '', onClose, onChanged }) {
  const [rows, setRows] = useState([])
  const [defaults, setDefaults] = useState({ area: initialArea, acao: '' })
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState('')
  const [stopping, setStopping] = useState(false)
  const [pendingExit, setPendingExit] = useState(null)
  const [waitingExit, setWaitingExit] = useState(false)
  const [catalogRevision, setCatalogRevision] = useState(0)
  const rowsRef = useRef(rows)
  const operation = useRef(null)
  const stopRequested = useRef(false)
  const exitAfterCurrent = useRef(null)
  const catalog = useAcervoCatalog('', catalogRevision)
  const dirty = rows.some((row) => !row.documento)
  const importable = rows.filter(canImportRow).length
  const indexable = rows.filter(canIndexRow).length
  const savedCount = rows.filter((row) => row.documento).length
  const readyCount = rows.filter((row) => row.status === 'ready').length
  const failedCount = rows.filter((row) => ['failed', 'invalid', 'uncertain'].includes(row.status)).length
  const activeRow = rows.find((row) => ['extracting', 'saving', 'indexing'].includes(row.status))
  const navigationBlocker = useBlocker(({ currentLocation, nextLocation }) =>
    Boolean(dirty || busy) && (currentLocation.pathname !== nextLocation.pathname ||
      currentLocation.search !== nextLocation.search || currentLocation.hash !== nextLocation.hash))

  const replaceRows = (next) => { rowsRef.current = next; setRows(next) }
  const patchRow = (id, patch) => replaceRows(rowsRef.current.map((row) => row.id === id ? { ...row, ...patch } : row))

  useEffect(() => () => { operation.current?.abort() }, [])
  useEffect(() => {
    if (!dirty && !busy) return undefined
    const beforeUnload = (event) => { event.preventDefault(); event.returnValue = '' }
    const beforeLogout = (event) => {
      if (typeof event.detail?.proceed !== 'function') return
      event.preventDefault()
      setPendingExit({ proceed: event.detail.proceed })
    }
    window.addEventListener('beforeunload', beforeUnload)
    window.addEventListener('auth:before-logout', beforeLogout)
    return () => {
      window.removeEventListener('beforeunload', beforeUnload)
      window.removeEventListener('auth:before-logout', beforeLogout)
    }
  }, [dirty, busy])

  const selectFiles = (event) => {
    const files = Array.from(event.target.files || [])
    event.target.value = ''
    if (!files.length || operation.current) return
    const result = appendBatchFiles(rowsRef.current, files, defaults, () => crypto.randomUUID())
    replaceRows(result.rows)
    setNotice([result.duplicates ? `${result.duplicates} arquivo(s) já presente(s) neste lote foram ignorados.` : '',
      result.excess ? `${result.excess} arquivo(s) excederam o limite de ${MAX_BATCH_FILES} por lote.` : ''].filter(Boolean).join(' '))
  }

  const editRow = (row, key, value) => patchRow(row.id, {
    fields: { ...row.fields, [key]: value }, error: '', status: 'pending',
  })
  const applyDefaults = () => replaceRows(rowsRef.current.map((row) => canImportRow(row)
    ? { ...row, fields: { ...row.fields, ...defaults }, error: '', status: 'pending' } : row))
  const requestStop = () => { stopRequested.current = true; setStopping(true) }
  const leave = () => dirty || busy ? setPendingExit({ proceed: onClose }) : onClose()
  const cancelExit = () => {
    exitAfterCurrent.current = null
    setWaitingExit(false)
    setPendingExit(null)
    if (navigationBlocker.state === 'blocked') navigationBlocker.reset()
  }
  const confirmExit = () => {
    const proceed = navigationBlocker.state === 'blocked' ? navigationBlocker.proceed : pendingExit?.proceed
    if (operation.current) {
      // Let the active mutation respond before leaving so saved originals are accounted for.
      exitAfterCurrent.current = proceed
      requestStop()
      setWaitingExit(true)
    } else { setPendingExit(null); proceed?.() }
  }

  const run = async (kind) => {
    if (operation.current) return
    const controller = new AbortController()
    operation.current = controller
    stopRequested.current = false
    setStopping(false)
    setBusy(kind)
    setNotice('')
    let changed = false
    try {
      const options = { api: PecasAcervo, extractPdf: extractPecaPdf, catalog, signal: controller.signal,
        shouldStop: () => stopRequested.current, onChange: patchRow, onSaved: () => { changed = true } }
      if (kind === 'import') await importBatch(rowsRef.current, options)
      else await indexBatch(rowsRef.current, options)
      if (!controller.signal.aborted) setNotice(stopRequested.current
        ? 'Lote interrompido após o arquivo atual. Os documentos salvos permanecem na base; você pode continuar os pendentes.'
        : kind === 'import' ? 'Importação finalizada. Confira o resultado de cada arquivo. A indexação é uma etapa opcional abaixo.'
          : 'Tentativa de indexação finalizada. Confira o resultado de cada peça.')
    } finally {
      operation.current = null
      if (changed) onChanged()
      if (!controller.signal.aborted) { setBusy(''); setStopping(false) }
      const proceed = exitAfterCurrent.current
      exitAfterCurrent.current = null
      proceed?.()
    }
  }

  return (
    <section className="acervo-section acervo-batch" aria-label="Importação de peças em lote">
      <header className="acervo-toolbar acervo-section-header">
        <div className="acervo-section-heading"><h2>Importar peças em lote</h2><p>Classifique os arquivos e importe os originais. Depois, escolha se deseja indexar as peças para usá-las como referência na redação.</p></div>
        <button className="acervo-button" onClick={leave}><i className="fas fa-arrow-left" aria-hidden="true" /> Voltar à base</button>
      </header>

      <section className="acervo-panel" aria-label="Classificação comum do lote">
        <h2>Classificação comum</h2>
        <p className="acervo-muted">Esta classificação será usada nos próximos arquivos selecionados. Você também pode aplicá-la às peças pendentes e ajustar cada linha antes de importar.</p>
        <fieldset className="acervo-batch-defaults" disabled={Boolean(busy)}>
          <label className="acervo-field">Área jurídica
            <select value={defaults.area} onChange={(event) => setDefaults((value) => ({ ...value, area: event.target.value }))}>
              <option value="">Selecione a área</option>{catalog.areas.map((area) => <option key={area.key} value={area.key}>{area.label}</option>)}
            </select>
          </label>
          <label className="acervo-field">Ação do motor
            <select value={defaults.acao} disabled={catalog.loading} onChange={(event) => setDefaults((value) => ({ ...value, acao: event.target.value }))}>
              <option value="">Selecione uma ação</option>{catalog.acoesCanonicas.map((action) => <option key={action.codigo} value={action.codigo}>{action.nome}</option>)}
            </select>
          </label>
          <button type="button" className="acervo-button" disabled={!defaults.area || !defaults.acao || !importable || catalog.loading || Boolean(catalog.error)} onClick={applyDefaults}>Aplicar às pendentes</button>
        </fieldset>
        {catalog.error && <p className="acervo-notice acervo-error" role="alert">{catalog.error}<button disabled={Boolean(busy)} onClick={() => setCatalogRevision((value) => value + 1)}>Recarregar ações</button></p>}
        <div className="acervo-upload">
          <i className="fas fa-file-circle-plus" aria-hidden="true" />
          <strong>Selecione vários arquivos de uma vez</strong>
          <span>PDF, DOCX ou TXT · até 10 MB por arquivo · até {MAX_BATCH_FILES} arquivos por lote</span>
          <label className={`acervo-button ${busy || rows.length >= MAX_BATCH_FILES ? 'is-disabled' : ''}`}>
            Selecionar arquivos<input type="file" multiple accept=".pdf,.docx,.txt" disabled={Boolean(busy) || rows.length >= MAX_BATCH_FILES} onChange={selectFiles} aria-label="Selecionar arquivos para importar em lote" />
          </label>
          <small>Os arquivos só serão enviados ao clicar em Importar. PDFs digitalizados podem precisar de texto adicionado depois; o original será preservado.</small>
        </div>
      </section>

      {notice && <p className="acervo-notice" role="status">{notice}</p>}
      {rows.length > 0 && <section className="acervo-list-panel" aria-label="Arquivos do lote">
        <div className="acervo-list-heading"><h3>{rows.length} arquivo(s) no lote</h3><span>{savedCount} salvo(s) · {readyCount} indexado(s) · {failedCount} com pendência</span></div>
        <ol className="acervo-batch-list">{rows.map((row) => {
          const editable = !busy && canImportRow(row)
          const locked = !editable
          return <li key={row.id} className="acervo-batch-row">
            <div className="acervo-batch-file"><strong>{row.file.name}</strong><span>{formatBytes(row.file.size)}</span>
              <span className={`acervo-status ${row.status === 'ready' || row.status === 'saved' ? 'acervo-status-pronto' : ['invalid', 'failed', 'uncertain'].includes(row.status) ? 'acervo-status-falhou' : 'acervo-status-pendente'}`}>{STATUS[row.status]}</span>
              {!busy && !row.documento && <button className="acervo-icon-button" aria-label={`Remover ${row.file.name} do lote`} onClick={() => replaceRows(rowsRef.current.filter((item) => item.id !== row.id))}><i className="fas fa-times" aria-hidden="true" /></button>}
            </div>
            <div className="acervo-batch-fields">
              <label className="acervo-field">Título da peça
                <input value={row.fields.titulo} disabled={locked} maxLength={240} onChange={(event) => editRow(row, 'titulo', event.target.value)} />
              </label>
              <label className="acervo-field">Área jurídica
                <select value={row.fields.area} disabled={locked} onChange={(event) => editRow(row, 'area', event.target.value)}>
                  <option value="">Selecione a área</option>{catalog.areas.map((area) => <option key={area.key} value={area.key}>{area.label}</option>)}
                </select>
              </label>
              <label className="acervo-field">Ação do motor
                <select value={row.fields.acao} disabled={locked || catalog.loading} onChange={(event) => editRow(row, 'acao', event.target.value)}>
                  <option value="">Selecione uma ação</option>{row.fields.acao && !catalog.acoesCanonicas.some((action) => action.codigo === row.fields.acao) && <option value={row.fields.acao}>{actionLabel(row.fields.acao)} (indisponível)</option>}
                  {catalog.acoesCanonicas.map((action) => <option key={action.codigo} value={action.codigo}>{action.nome}</option>)}
                </select>
              </label>
            </div>
            {row.progress && <p className="acervo-working">Lendo página {row.progress.page} de {row.progress.total}…</p>}
            {row.error && <p className="acervo-notice acervo-error" role="alert">{row.error}</p>}
            {row.warnings.map((warning, index) => <p key={index} className="acervo-notice">{warning}</p>)}
          </li>
        })}</ol>
      </section>}

      <div className="acervo-panel acervo-batch-actions">
        <div className="acervo-toolbar">
          <button className="acervo-button acervo-primary" disabled={Boolean(busy) || !importable || catalog.loading || Boolean(catalog.error)} onClick={() => run('import')}><i className="fas fa-upload" aria-hidden="true" /> {savedCount || failedCount ? `Importar pendentes (${importable})` : `Importar arquivos (${importable})`}</button>
          <button className="acervo-button" disabled={Boolean(busy) || !indexable} onClick={() => run('index')}><i className="fas fa-wand-magic-sparkles" aria-hidden="true" /> Indexar peças salvas ({indexable})</button>
          {busy && <button className="acervo-button" disabled={stopping} onClick={requestStop}>{stopping ? 'Aguardando arquivo atual…' : 'Parar após arquivo atual'}</button>}
        </div>
        <p className="acervo-muted">A importação salva os originais sem depender da IA. A indexação é opcional e usa os modelos configurados na base jurídica. Repetir uma etapa mantém os arquivos já salvos e processa apenas os itens pendentes.</p>
        {busy && <div className="acervo-batch-progress" role="status" aria-live="polite"><i className="fas fa-spinner fa-spin" aria-hidden="true" /><span>{stopping ? 'Interrompendo após o arquivo atual.' : busy === 'import' ? 'Importação em andamento.' : 'Indexação em andamento.'} {activeRow ? activeRow.file.name : ''}</span></div>}
      </div>

      <Modal isOpen={Boolean(pendingExit) || navigationBlocker.state === 'blocked'} onClose={cancelExit} title="Sair da importação em lote?" size="small" icon="fa-exclamation-triangle"
        footer={<><button className="btn btn-outline" onClick={cancelExit}>Permanecer</button><button className="btn btn-danger" disabled={waitingExit} onClick={confirmExit}>{waitingExit ? 'Aguardando arquivo atual…' : busy ? 'Parar e sair' : 'Sair do lote'}</button></>}>
        <p>Os arquivos já salvos permanecerão na base jurídica. Ao sair, a seleção e as alterações dos itens ainda não importados serão descartadas.</p>
        {busy && <p>Aguarde a resposta do arquivo atual para confirmar seu resultado. Os próximos arquivos não serão processados após confirmar a saída.</p>}
      </Modal>
    </section>
  )
}
